import { getAverageClosedAccountsChart, padWithZeroes } from '@components/decay/decay-chart-options';
import { IDecayCalibration } from '@models/decay/decay-calibration';
import { DecayCalibrationType } from '@models/decay/decay-calibration-type';
import { IDecayClosedAccount } from '@models/decay/decay-closed-account';
import { IDecayEstimate } from '@models/decay/decay-estimate';
import { IDecayExportItem } from '@models/export/export-item';
import { getDecayCalibrationTypeSettings } from '@services/decay/decay-calibration-strings';
import { filterNegativesFromResult } from '@shared/decay-helpers';
import * as Highcharts from 'highcharts';
import { IExportChartOption } from '../export.config';
import { sortEstimates } from '../spread/decay/decay-estimate-detail';

export const decayChartJSON = (
  decayItem: IDecayExportItem,
  estimates: IDecayEstimate[],
  exportChartOption: IExportChartOption,
  calibration: IDecayCalibration,
  closedAccounts: IDecayClosedAccount[] = null
) => {
  if (calibration.DecayCalibrationType === DecayCalibrationType.AvgClosedAccounts) {
    closedAccounts.pop();
    return getAverageClosedAccountsChart(closedAccounts, decayItem.ApprovedDecayFactors, exportChartOption);
  }

  const decayCalibrationTypeFromEstimate = estimates.length ? estimates[0].DecayCalibrationType || 1 : 1;

  const chartObj = {
    exporting: {
      sourceWidth: exportChartOption.width,
      sourceHeight: exportChartOption.height,
      allowHTML: true
    },
    type: 'column',
    zoomType: 'x',
    title: {
      text: null
    },
    yAxis: [
      {
        gridLineWidth: 1,
        ...getDecayCalibrationTypeSettings(decayCalibrationTypeFromEstimate)
      }
    ],
    xAxis: {
      min: 1
    },
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      alignColumns: true,
      itemWidth: 160
    },
    plotOptions: {
      marker: {
        enabled: false
      }
    },
    tooltip: {
      enabled: true,
      formatter() {
        const value = this.y.toFixed(2);
        return `x: ${this.x}<br><span style="color:${this.point.color}">●</span> <b>${this.series.name}:</b> ${value}`;
      }
    },
    series: [],
    credits: {
      enabled: false
    },
    navigation: {
      buttonOptions: {
        enabled: true
      }
    }
  };

  const filteredResults = filterNegativesFromResult(decayItem.DecayResult);

  // averageVintageBalances.ObservationAverages = averageVintageBalances.ObservationAverages.filter(o => o.Observation > 0);

  const vintageObsData = [];
  filteredResults.ObservationAverages.forEach((vintageRow, index) => {
    vintageObsData.push([index + 1, vintageRow.Value]);
  });

  const averageVintageBalancesOptions = {
    name: getDecayCalibrationTypeSettings(decayCalibrationTypeFromEstimate).title,
    color: Highcharts.getOptions().colors[0],
    yAxis: 0,
    data: vintageObsData,
    type: 'column'
  };

  chartObj.series.push(averageVintageBalancesOptions);

  // Adding estimates
  estimates = sortEstimates(estimates);
  estimates.forEach((est, index) => {
    est = padWithZeroes(est, filteredResults.ObservationAverages.length);

    const data =
      est &&
      est.DecayResult &&
      est.DecayResult.DecayEstimateResults &&
      est.DecayResult.DecayEstimateResults.map((bal, ind) => [ind + 1, bal.Result]);

    const chosenEstimateColour = 'green'; // est.Colour

    // Build up our object
    const lineObject = {
      id: est.Id,
      type: 'line',
      dashStyle: 'Dash',
      color: est.IsChosen ? chosenEstimateColour : est.Colour,
      textColor: chosenEstimateColour,
      connectEnds: true,
      name: est.IsChosen ? `Estimate ${(index += 1)} *` : `Estimate ${(index += 1)}`,
      data,
      marker: {
        enabled: false
      }
    };

    // Push to the chart

    // @ts-ignore
    chartObj.series.push(lineObject);
  });

  return chartObj;
};
